import { FC, Fragment, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import * as Survey from 'survey-react';
import 'survey-react/survey.css';
// import './survey.css';
import SurveyPDF from './SurveyPDF';
import { configureSurveyDropdowns } from '../../containers/shared/admin/survey/survey';
import { SCREEN } from '../../theme/breakpoints';

const isWeb = SCREEN.isWeb;

type Props = {
  exportPDFTitle?: any;
  data: any;
  ecdRegistrationId?: string;
  institutionId?: string;
  readOnly?: boolean;
  questions: any;
  onAfterRenderQuestion?: any;
  onComplete?: any;
  onValueChange?: any;
  onActivityTimeout?: any;
};

type WrapperProps = {
  isWeb: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  .sv-title {
    width: 70%;
  }
  .sd-page {
    max-width: 100% !important;
  }
  .sd-body__page {
    max-width: 100% !important;
  }

  ${props =>
    !props.isWeb &&
    css`
      .sd-clearfix {
        display: flex;
        flex-direction: column;
      }
      .sv-action {
        width: 90%;
      }
      .sd-btn {
        width: 100%;
        margin-top: 10px;
        margin-bottom: 10px;
        margin-left: -20px;
      }
    `}
`;
const SurveyComponent: FC<Props> = ({
  exportPDFTitle,
  data,
  ecdRegistrationId,
  institutionId,
  readOnly,
  questions,
  onAfterRenderQuestion,
  onComplete,
  onValueChange,
  onActivityTimeout,
}) => {
  const savedAnswers = localStorage.getItem(`currentAnswers-${institutionId}-${ecdRegistrationId}`);
  const answerObj = !!savedAnswers ? JSON.parse(savedAnswers) : {};
  const [surveyData, setSurveyData] = useState({ ...data, ...answerObj });

  useEffect(() => {
    const savedAnswers = localStorage.getItem(`currentAnswers-${institutionId}-${ecdRegistrationId}`);
    const answerObj = !!savedAnswers ? JSON.parse(savedAnswers) : {};

    setSurveyData({ ...surveyData, ...answerObj });
  }, [localStorage.getItem(`currentAnswers-${institutionId}-${ecdRegistrationId}`)]);

  useEffect(() => {
    const savedPage = localStorage.getItem(`currentPage-${institutionId}-${ecdRegistrationId}`);

    model.currentPage = Number(savedPage);
  }, [localStorage.getItem(`currentPage-${institutionId}-${ecdRegistrationId}`)]);

  // survey config
  // Survey.StylesManager.applyTheme('modern');
  Survey.StylesManager.applyTheme('defaultV2');

  Survey.JsonObject.metaData.addProperty('question', 'questionNumber');
  Survey.JsonObject.metaData.addProperty('question', 'category');
  Survey.JsonObject.metaData.addProperty('question', { name: 'id:string' });
  Survey.JsonObject.metaData.addProperty('itemvalue', { name: 'id:string' });

  //Add a custom 'requirePhotos' property to questions of all types and to pages
  Survey.Serializer.addProperty('question', 'requirePhotos:text');
  Survey.Serializer.addProperties('question', [
    {
      name: 'questionNumber',
      category: 'general',
    },
    {
      name: 'tag',
      category: 'general',
    },
  ]);
  Survey.Serializer.findProperty('survey', 'clearInvisibleValues').defaultValue = 'onHidden';

  const model = new Survey.Model(questions);

  useEffect(() => {
    let inactivityTimeout: any;

    // Function to show the alert
    const showInactivityAlert = async () => {
      const registrationAnswersJson = localStorage.getItem(`currentAnswers-${institutionId}-${ecdRegistrationId}`);

      if (registrationAnswersJson) {
        try {
          const registrationAnswers = JSON.parse(registrationAnswersJson);
          let answersObject = {};
          Object.entries(registrationAnswers).map(([key, value]: any) => {
            const question = model.getQuestionByName(key);
            if (question) {
              answersObject['tag:' + question['tag']] = value;
            }
          });
          if (onActivityTimeout) {
            onActivityTimeout(answersObject);
          }
        } catch (error) {
          console.log(error);
        }
      }
    };

    // Start the inactivity timer when the component mounts
    inactivityTimeout = setTimeout(showInactivityAlert, 60000); // 60 seconds

    // Reset the timer if the user interacts with the page
    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimeout);
      inactivityTimeout = setTimeout(showInactivityAlert, 60000); // 60 seconds
    };

    // Listen for user activity events (e.g., mousemove, keydown)
    window.addEventListener('mousemove', resetInactivityTimer);
    window.addEventListener('keydown', resetInactivityTimer);

    // Clean up the event listeners when the component unmounts
    return () => {
      clearTimeout(inactivityTimeout);
      window.removeEventListener('mousemove', resetInactivityTimer);
      window.removeEventListener('keydown', resetInactivityTimer);
    };
  }, []);

  model.onComplete.add(async (result: any) => {
    let nonHiddenTags: string[] = [];
    Object.entries(model.data).forEach(([name, answer]) => {
      const question = model.getQuestionByName(name);

      if (question) {
        model.setValue('tag:' + question['tag'], answer);
        nonHiddenTags.push(question['tag']);
      }
    });
    const tagsArray = Object.values(model.data)
      .filter((a: any) => typeof a === 'object')
      .map((b: any) => b && b.question && b.question.tag)
      .filter((c: any) => !!c);

    tagsArray.forEach((b: string) => {
      if (!nonHiddenTags.includes(b)) {
        model.setValue('tag:' + b, 'undefined');
      }
    });
  });

  onComplete && model.onComplete.add(onComplete);
  onAfterRenderQuestion && model.onAfterRenderQuestion.add(onAfterRenderQuestion);
  model.onValueChanged.add(async (ans: any, options: any) => {
    const question = model.getQuestionByName(options.name);

    if (question) {
      const value = { ['tag:' + question['tag']]: options.value };
      onValueChange(value);
      localStorage.setItem(`currentAnswers-${institutionId}-${ecdRegistrationId}`, JSON.stringify(ans.valuesHash));
    }
  });

  model.onCurrentPageChanged.add(() =>
    localStorage.setItem(
      `currentPage-${institutionId}-${ecdRegistrationId}`,
      String(model.currentPage.propertyHash.visibleIndex)
    )
  );

  configureSurveyDropdowns(model);

  model.addNavigationItem({
    id: 'last_page',
    title: '>>',
    visibleIndex: 31,
    action: () => {
      for (let i = 0; i < model.pageCount; i++) {
        const newPage = model.pages[i];
        model.currentPage = newPage;
        if (model.isCurrentPageHasErrors) {
          break;
        }
      }
    },
    // @ts-ignore
    visible: new Survey.ComputedUpdater(() => {
      return !model.isLastPage;
    }),
  });

  model.addNavigationItem({
    id: 'first_page',
    title: '<<',
    visibleIndex: 1,
    // innerCss: 'sv-btn sv-btn--navigation sv-footer__prev-btn',
    action: () => {
      const newPage = model.pages[0];
      model.currentPage = newPage;
    },
    // @ts-ignore
    visible: new Survey.ComputedUpdater(() => {
      return !model.isFirstPage;
    }),
  });

  model.currentPage = Number(localStorage.getItem(`currentPage-${institutionId}-${ecdRegistrationId}`)) || 0;

  if (readOnly) {
    model.mode = 'display';
  }

  return (
    <Fragment>
      <SurveyPDF json={questions} data={surveyData} exportPDFTitle={exportPDFTitle} />
      <Wrapper isWeb={isWeb}>
        <Survey.Survey data={surveyData} model={model} readOnly={readOnly} />
      </Wrapper>
    </Fragment>
  );
};

export default SurveyComponent;
